import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    empresa: '',
    faturamento: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isScrolled, setIsScrolled] = useState(false);

  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('nome', formData.nome);
      formBody.append('email', formData.email);
      formBody.append('empresa', formData.empresa);
      formBody.append('faturamento', formData.faturamento);

      const response = await fetch('https://readdy.ai/api/form/d5p1ji71urhk7g0fu1d0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ nome: '', email: '', empresa: '', faturamento: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between">
          <img 
            src="https://static.readdy.ai/image/8e1dd29798fcb43b2566dc879e04c9bd/4ec2bf74607ae5a6da072028c7b40c06.png" 
            alt="The One Publicidade e Marketing" 
            className="h-8 sm:h-12"
          />
          <button className="px-4 sm:px-8 py-2 sm:py-3 bg-black text-white rounded-full text-xs sm:text-sm font-medium hover:bg-gray-900 transition-all whitespace-nowrap cursor-pointer">
            Falar com especialista
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center px-4 sm:px-6 pt-24 sm:pt-32 pb-16 sm:pb-20 bg-gradient-to-b from-gray-50/50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center px-4 sm:px-6 py-2 border border-black rounded-full mb-8 sm:mb-12">
            <span className="text-xs sm:text-sm font-medium">Marketing 360°</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-black mb-8 sm:mb-12 leading-[1.1] tracking-tight px-4">
            Marketing 360 para empresas<br className="hidden sm:block" />
            que buscam <span className="font-light italic text-gray-600">crescimento previsível</span>
          </h1>
          
          <p className="text-base sm:text-xl text-gray-700 mb-10 sm:mb-14 max-w-2xl mx-auto leading-relaxed px-4">
            Estratégia integrada de marketing para negócios que faturam acima de R$30 mil por mês e querem escalar com método.
          </p>
          
          <button 
            onClick={() => document.getElementById('form-section')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 sm:gap-3 px-8 sm:px-12 py-4 sm:py-5 bg-black text-white rounded-full text-sm sm:text-base font-medium hover:shadow-2xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
          >
            Solicitar diagnóstico estratégico
            <i className="ri-arrow-right-line text-base sm:text-lg"></i>
          </button>
          
          <p className="text-xs sm:text-sm text-gray-500 mt-4 sm:mt-6 px-4">
            Atendimento nacional • Vagas limitadas por projeto
          </p>
        </div>
      </section>

      {/* Bloco de Dor */}
      <section className="py-16 sm:py-32 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-[#FAF8F5] rounded-3xl sm:rounded-[32px] px-6 sm:px-24 py-12 sm:py-20">
            <h2 className="text-3xl sm:text-5xl font-bold text-black text-center mb-10 sm:mb-16">
              Crescer sem previsibilidade custa caro
            </h2>
            
            <p className="text-base sm:text-xl text-gray-700 text-center mb-10 sm:mb-16 max-w-3xl mx-auto">
              Empresas em crescimento enfrentam desafios comuns:
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 mb-10 sm:mb-14">
              <div className="bg-white border border-gray-200 rounded-2xl sm:rounded-3xl p-6 sm:p-10">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-4 sm:mb-7">
                  <i className="ri-links-line text-3xl sm:text-4xl text-black"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-black">Marketing desconectado</h3>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-2xl sm:rounded-3xl p-6 sm:p-10">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-4 sm:mb-7">
                  <i className="ri-question-line text-3xl sm:text-4xl text-black"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-black">Falta de clareza sobre ROI</h3>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-2xl sm:rounded-3xl p-6 sm:p-10">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-4 sm:mb-7">
                  <i className="ri-emotion-line text-3xl sm:text-4xl text-black"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-black">Decisões baseadas em feeling</h3>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-2xl sm:rounded-3xl p-6 sm:p-10">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-4 sm:mb-7">
                  <i className="ri-line-chart-line text-3xl sm:text-4xl text-black"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-black">Crescimento instável</h3>
              </div>
            </div>
            
            <p className="text-lg sm:text-2xl italic text-gray-800 text-center max-w-2xl mx-auto leading-relaxed">
              Marketing precisa ser um <strong className="font-bold text-black not-italic">sistema estruturado</strong>, não um conjunto de ações isoladas.
            </p>
          </div>
        </div>
      </section>

      {/* Bloco de Equipe */}
      <section className="py-16 sm:py-32 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center px-4 sm:px-5 py-2 border border-gray-300 rounded-full mb-4 sm:mb-6">
              <span className="text-xs sm:text-sm text-gray-600">Quem Está Por Trás</span>
            </div>
            
            <h3 className="text-3xl sm:text-5xl font-bold text-black mb-6 sm:mb-8 leading-tight px-4">
              Especialistas à frente<br />da estratégia
            </h3>
            
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-10 sm:mb-16 max-w-3xl mx-auto px-4">
              A The One Publicidade e Marketing é liderada por sócios com visão estratégica de negócios, focados em <strong className="font-bold text-black">crescimento previsível</strong>, decisões orientadas por <strong className="font-bold text-black">dados</strong> e marketing como <strong className="font-bold text-black">ativo de escala</strong> — não como custo operacional.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-10 max-w-5xl mx-auto">
            <div className="rounded-3xl sm:rounded-[32px] overflow-hidden h-[500px] sm:h-[600px]">
              <img 
                src="https://static.readdy.ai/image/8e1dd29798fcb43b2566dc879e04c9bd/210820291cc4f2a393e0e896eed57fa0.png" 
                alt="Sócio fundador The One" 
                className="w-full h-full object-cover object-top"
              />
            </div>
            
            <div className="rounded-3xl sm:rounded-[32px] overflow-hidden h-[500px] sm:h-[600px]">
              <img 
                src="https://static.readdy.ai/image/8e1dd29798fcb43b2566dc879e04c9bd/ad78d9501609417c97c7db0fd75d87c6.jpeg" 
                alt="Sócio fundador The One" 
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          
          <div className="text-center mt-8 sm:mt-12">
            <div className="inline-block pt-6 sm:pt-10 border-t border-gray-300">
              <p className="text-xs sm:text-sm text-gray-500">
                Sócios fundadores — The One Publicidade e Marketing
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Serviços Marketing 360 */}
      <section className="py-16 sm:py-32 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-20">
            <div className="inline-flex items-center px-4 sm:px-5 py-2 border border-gray-300 rounded-full mb-4 sm:mb-5">
              <span className="text-xs sm:text-sm text-gray-600">O Que Fazemos</span>
            </div>
            <h2 className="text-3xl sm:text-6xl font-bold text-black px-4">
              Marketing integrado para<br className="hidden sm:block" />crescimento sustentável
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-gray-100 rounded-2xl sm:rounded-3xl p-8 sm:p-12 hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-5 sm:mb-7">
                <i className="ri-lightbulb-line text-4xl sm:text-5xl text-black"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-black">
                Estratégia e planejamento<br />de crescimento
              </h3>
            </div>
            
            <div className="bg-gray-100 rounded-2xl sm:rounded-3xl p-8 sm:p-12 hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-5 sm:mb-7">
                <i className="ri-advertisement-line text-4xl sm:text-5xl text-black"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-black">
                Gestão de tráfego pago<br />com foco em eficiência
              </h3>
            </div>
            
            <div className="bg-gray-100 rounded-2xl sm:rounded-3xl p-8 sm:p-12 hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-5 sm:mb-7">
                <i className="ri-search-line text-4xl sm:text-5xl text-black"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-black">
                SEO estratégico e<br />conteúdo de autoridade
              </h3>
            </div>
            
            <div className="bg-gray-100 rounded-2xl sm:rounded-3xl p-8 sm:p-12 hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-5 sm:mb-7">
                <i className="ri-cursor-line text-4xl sm:text-5xl text-black"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-black">
                Conversão (landing pages<br />e CRO)
              </h3>
            </div>
            
            <div className="bg-gray-100 rounded-2xl sm:rounded-3xl p-8 sm:p-12 hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-5 sm:mb-7">
                <i className="ri-bar-chart-box-line text-4xl sm:text-5xl text-black"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-black">
                Análise de dados e<br />otimização contínua
              </h3>
            </div>
            
            <div className="bg-gray-100 rounded-2xl sm:rounded-3xl p-8 sm:p-12 hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-5 sm:mb-7">
                <i className="ri-team-line text-4xl sm:text-5xl text-black"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-black">
                Consultoria estratégica<br />personalizada
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* Metodologia */}
      <section className="py-16 sm:py-32 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-black rounded-3xl sm:rounded-[48px] px-6 sm:px-32 py-12 sm:py-24">
            <h2 className="text-3xl sm:text-5xl font-bold text-white text-center mb-3 sm:mb-4">
              Método claro. Decisões melhores.
            </h2>
            
            <p className="text-base sm:text-xl text-gray-300 text-center mb-10 sm:mb-16">
              Nada de achismo. Tudo é orientado por dados.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10">
              <div className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-white rounded-full">
                <span className="text-base sm:text-lg font-medium text-white whitespace-nowrap">Diagnóstico</span>
              </div>
              
              <div className="w-0.5 h-6 sm:w-10 sm:h-0.5 bg-white/60"></div>
              
              <div className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-white rounded-full">
                <span className="text-base sm:text-lg font-medium text-white whitespace-nowrap">Estratégia</span>
              </div>
              
              <div className="w-0.5 h-6 sm:w-10 sm:h-0.5 bg-white/60"></div>
              
              <div className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-white rounded-full">
                <span className="text-base sm:text-lg font-medium text-white whitespace-nowrap">Execução</span>
              </div>
              
              <div className="w-0.5 h-6 sm:w-10 sm:h-0.5 bg-white/60"></div>
              
              <div className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-white rounded-full">
                <span className="text-base sm:text-lg font-medium text-white whitespace-nowrap">Otimização</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final + Formulário */}
      <section id="form-section" className="py-16 sm:py-32 px-4 sm:px-6 bg-gradient-radial from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-6xl font-bold text-black mb-4 sm:mb-6 px-4">
            Avalie a maturidade do<br className="hidden sm:block" />seu marketing
          </h2>
          
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-10 sm:mb-16 max-w-3xl mx-auto px-4">
            Agende um diagnóstico estratégico gratuito e entenda onde estão os gargalos e oportunidades reais do seu crescimento.
          </p>
          
          <div className="bg-white border border-gray-200 rounded-3xl sm:rounded-[32px] p-6 sm:p-16 shadow-lg max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} data-readdy-form id="diagnostico-form">
              <div className="space-y-5 sm:space-y-6">
                <div className="text-left">
                  <label htmlFor="nome" className="block text-sm font-medium text-gray-800 mb-2">
                    Nome completo
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    className="w-full h-12 sm:h-14 px-4 sm:px-5 border-2 border-gray-300 rounded-xl text-sm sm:text-base focus:border-black focus:outline-none transition-colors"
                    placeholder="Seu nome"
                  />
                </div>
                
                <div className="text-left">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-800 mb-2">
                    Email profissional
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full h-12 sm:h-14 px-4 sm:px-5 border-2 border-gray-300 rounded-xl text-sm sm:text-base focus:border-black focus:outline-none transition-colors"
                    placeholder="seu@email.com"
                  />
                </div>
                
                <div className="text-left">
                  <label htmlFor="empresa" className="block text-sm font-medium text-gray-800 mb-2">
                    Empresa
                  </label>
                  <input
                    type="text"
                    id="empresa"
                    name="empresa"
                    value={formData.empresa}
                    onChange={handleChange}
                    required
                    className="w-full h-12 sm:h-14 px-4 sm:px-5 border-2 border-gray-300 rounded-xl text-sm sm:text-base focus:border-black focus:outline-none transition-colors"
                    placeholder="Nome da sua empresa"
                  />
                </div>
                
                <div className="text-left">
                  <label htmlFor="faturamento" className="block text-sm font-medium text-gray-800 mb-2">
                    Faturamento mensal
                  </label>
                  <select
                    id="faturamento"
                    name="faturamento"
                    value={formData.faturamento}
                    onChange={handleChange}
                    required
                    className="w-full h-12 sm:h-14 px-4 sm:px-5 border-2 border-gray-300 rounded-xl text-sm sm:text-base focus:border-black focus:outline-none transition-colors cursor-pointer"
                  >
                    <option value="">Selecione uma faixa</option>
                    <option value="R$ 30-50 mil">R$ 30-50 mil</option>
                    <option value="R$ 50-100 mil">R$ 50-100 mil</option>
                    <option value="R$ 100-250 mil">R$ 100-250 mil</option>
                    <option value="R$ 250-500 mil">R$ 250-500 mil</option>
                    <option value="Acima de R$ 500 mil">Acima de R$ 500 mil</option>
                  </select>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 sm:h-16 bg-black text-white rounded-2xl text-base sm:text-lg font-medium mt-6 sm:mt-8 hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Enviando...' : 'Solicitar diagnóstico estratégico'}
                {!isSubmitting && <i className="ri-arrow-right-line text-lg sm:text-xl"></i>}
              </button>
              
              {submitStatus === 'success' && (
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-green-800 text-xs sm:text-sm font-medium">
                    ✓ Solicitação enviada com sucesso! Entraremos em contato em breve.
                  </p>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-red-800 text-xs sm:text-sm font-medium">
                    Erro ao enviar. Por favor, tente novamente.
                  </p>
                </div>
              )}
              
              <div className="flex items-center justify-center gap-2 mt-3 sm:mt-4">
                <i className="ri-lock-line text-gray-400 text-xs sm:text-sm"></i>
                <p className="text-xs text-gray-500">
                  Seus dados são utilizados exclusivamente para contato profissional.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black rounded-t-3xl sm:rounded-t-[48px] mt-20 sm:mt-36 px-6 sm:px-24 py-12 sm:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-16 mb-12 sm:mb-16">
            <div>
              <img 
                src="https://static.readdy.ai/image/8e1dd29798fcb43b2566dc879e04c9bd/4ec2bf74607ae5a6da072028c7b40c06.png" 
                alt="The One" 
                className="h-8 sm:h-10 mb-4 sm:mb-6 brightness-0 invert"
              />
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
                Marketing 360° para empresas que buscam crescimento previsível e sustentável.
              </p>
            </div>
            
            <div>
              <h4 className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider mb-4 sm:mb-6">Serviços</h4>
              <ul className="space-y-2 sm:space-y-3">
                <li><a href="#" className="text-sm sm:text-base text-white hover:text-gray-300 transition-colors cursor-pointer">Estratégia de Marketing</a></li>
                <li><a href="#" className="text-sm sm:text-base text-white hover:text-gray-300 transition-colors cursor-pointer">Tráfego Pago</a></li>
                <li><a href="#" className="text-sm sm:text-base text-white hover:text-gray-300 transition-colors cursor-pointer">SEO &amp; Conteúdo</a></li>
                <li><a href="#" className="text-sm sm:text-base text-white hover:text-gray-300 transition-colors cursor-pointer">Conversão &amp; CRO</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider mb-4 sm:mb-6">Contato</h4>
              <ul className="space-y-2 sm:space-y-3">
                <li>
                  <a href="mailto:contato@theonepublicidade.com.br" className="text-sm sm:text-base text-white hover:text-gray-300 transition-colors cursor-pointer">
                    contato@theonepublicidade.com.br
                  </a>
                </li>
                <li>
                  <a href="https://wa.me/5517992623414" target="_blank" rel="noopener noreferrer" className="text-sm sm:text-base text-white hover:text-gray-300 transition-colors cursor-pointer">
                    +55 17 99262-3414
                  </a>
                </li>
                <li className="text-gray-400 text-xs sm:text-sm mt-3 sm:mt-4">
                  Atendimento nacional<br />
                  Segunda a sexta, 9h às 18h
                </li>
              </ul>
            </div>
          </div>
          
          <div className="pt-6 sm:pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-6 sm:gap-0">
            <div className="flex items-center gap-6">
              <a href="https://www.linkedin.com/in/caique-ribeiro-0265a429a/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                <i className="ri-linkedin-fill text-lg sm:text-xl"></i>
              </a>
              <a href="https://www.instagram.com/theonepublicidade" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                <i className="ri-instagram-line text-lg sm:text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-lg sm:text-xl"></i>
              </a>
            </div>
            
            <p className="text-xs sm:text-sm text-gray-500 text-center sm:text-left order-3 sm:order-2">
              © 2025 The One Publicidade e Marketing. Todos os direitos reservados.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 order-2 sm:order-3">
              <a href="#" className="text-xs sm:text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">Política de Privacidade</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
